import React, { useState } from "react";
import { Eye, EyeOff, Mail, Lock, ArrowLeft } from "lucide-react";
import logoRedMimbre from "../assets/red-mimbre-logo-1.png";
import logoAmoverse from "../assets/ent/LOGO_AMOVERSE.png";
import logoCentreSantJaume from "../assets/ent/LOGO_CENTRE SANT JAUME.png";
import logoHogarSanJose from "../assets/ent/LOGO_HOGAR SAN JOSÉ.png";
import logoNazaret from "../assets/ent/LOGO_NAZARET.png";
import logoLaVinya from "../assets/ent/LOGO_LA VINYA.png";
import logoSalutAlta from "../assets/ent/LOGO_SALUT ALTA.png";
import logoSectorSocial from "../assets/ent/LOGO_SECTOR SOCIAL.png";
import logoPadreMontalvo from "../assets/ent/LOGO_PADRE MONTALVO.png";

interface LoginProps {
  onLogin: (email: string, password: string) => Promise<void>;
  language: "es" | "ca";
  onLanguageChange: (lang: "es" | "ca") => void;
}

export default function Login({
  onLogin,
  language,
}: LoginProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const translations = {
    es: {
      welcome: "Bienvenido a RedMimbrer - Encuestas",
      subtitle: "Plataforma de Encuestas Sociales",
      email: "Correo electrónico",
      password: "Contraseña",
      login: "Iniciar sesión",
      forgotPassword: "Olvidé mi contraseña",
      backToLogin: "Volver al inicio de sesión",
      resetPassword: "Restablecer contraseña",
      resetSubtitle: "Te enviaremos un enlace para restablecer tu contraseña",
      sendResetLink: "Enviar enlace",
      resetSent: "Enlace enviado",
      resetSentMessage:
        "Si el correo existe en nuestro sistema, recibirás un enlace para restablecer tu contraseña.",
      emailRequired: "El correo electrónico es obligatorio",
      passwordRequired: "La contraseña es obligatoria",
      invalidCredentials: "Credenciales incorrectas",
      enterEmail: "Ingresa tu correo electrónico",
      enterPassword: "Ingresa tu contraseña",
      showPassword: "Mostrar contraseña",
      hidePassword: "Ocultar contraseña",
      loggingIn: "Iniciando sesión...",
      sending: "Enviando...",
      footer: "© 2025 REDMIMBRE. Todos los derechos reservados.",
      userInactive:
        "El usuario está inactivo. Por favor, contacta al administrador.",
      noOrganization:
        "El usuario no pertenece a ninguna organización. Por favor, contacta al administrador.",
      organizationInactive:
        "La organización del usuario está inactiva. Por favor, contacta al administrador.",
      // New UI strings
      hello: "Herramienta online para la medición de impacto social",
      welcomeBack: "Bienvenido de nuevo",
      createAccountPrompt:
        "¿No tienes una cuenta? No te preocupes, ponte en contacto con Red Mimbre para que puedan gestionarte el alta en la plataforma.",
      brandDescription:
        "Automatiza y gestiona tus encuestas sociales de manera eficiente. Convierte datos en decisiones inteligentes y ahorra tiempo valioso.",
      forgotQuestion: "¿Olvidaste tu contraseña?",
      clickHere: "Haz clic aquí",
      accessibleApp: "Aplicación accesible diseñada para niños, niñas, adolescentes y familias",
    },
    ca: {
      welcome: "Benvingut a REDMIMBRE",
      subtitle: "Plataforma d'Enquestes Socials",
      email: "Correu electrònic",
      password: "Contrasenya",
      login: "Iniciar sessió",
      forgotPassword: "He oblidat la meva contrasenya",
      backToLogin: "Tornar a l'inici de sessió",
      resetPassword: "Restablir contrasenya",
      resetSubtitle: "T'enviarem un enllaç per restablir la teva contrasenya",
      sendResetLink: "Enviar enllaç",
      resetSent: "Enllaç enviat",
      resetSentMessage:
        "Si el correu existeix al nostre sistema, rebràs un enllaç per restablir la teva contrasenya.",
      emailRequired: "El correu electrònic és obligatori",
      passwordRequired: "La contrasenya és obligatòria",
      invalidCredentials: "Credencials incorrectes",
      enterEmail: "Introdueix el teu correu electrònic",
      enterPassword: "Introdueix la teva contrasenya",
      showPassword: "Mostrar contrasenya",
      hidePassword: "Ocultar contrasenya",
      loggingIn: "Iniciant sessió...",
      sending: "Enviant...",
      footer: "© 2024 REDMIMBRE. Tots els drets reservats.",
      userInactive:
        "L'usuari està inactiu. Si us plau, contacta amb l'administrador.",
      noOrganization:
        "L'usuari no pertany a cap organització. Si us plau, contacta amb l'administrador.",
      organizationInactive:
        "L'organització de l'usuari està inactiva. Si us plau, contacta amb l'administrador.",
      // New UI strings
      hello: "Hola!",
      welcomeBack: "Benvingut de nou",
      createAccountPrompt:
        "No tens un compte? Crea un compte nou ara, és GRATUÏT! Trigues menys d'un minut.",
      brandDescription:
        "Automatitza i gestiona les teves enquestes socials de manera eficient. Converteix dades en decisions intel·ligents i estalvia temps valuós.",
      forgotQuestion: "Has oblidat la teva contrasenya?",
      clickHere: "Fes clic aquí",
      accessibleApp: "Aplicació accessible dissenyada per a nens, nenes, adolescents i famílies",
    },
  };

  const t = translations[language];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = t.emailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Formato de correo inválido";
    }

    if (!showForgotPassword && !formData.password.trim()) {
      newErrors.password = t.passwordRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      if (showForgotPassword) {
        // Simulate password reset
        await new Promise((resolve) => setTimeout(resolve, 1500));
        setShowForgotPassword(false);
        // Show success message (in real app, would show a success state)
        alert(t.resetSentMessage);
      } else {
        await onLogin(formData.email, formData.password);
      }
    } catch (error: unknown) {
      console.error("Login error:", error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      if (
        errorMessage ===
        "System.UnauthorizedAccessException: El usuario está inactivo."
      ) {
        setErrors({ general: t.userInactive });
      } else if (
        errorMessage ===
        "System.UnauthorizedAccessException: La organización del usuario está inactiva."
      ) {
        setErrors({ general: t.organizationInactive });
      } else {
        setErrors({ general: t.invalidCredentials });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding Section */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-900 via-teal-900 to-green-900 relative overflow-hidden">
        {/* Subtle Pattern Background */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
        </div>
        
        {/* Decorative Curved Lines */}
        <div className="absolute top-0 left-0 w-full h-full">
          <svg className="w-full h-full opacity-20" viewBox="0 0 1000 1000" preserveAspectRatio="none">
            <path
              d="M0,200 Q250,100 500,200 T1000,200"
              fill="none"
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="2"
              className="animate-pulse"
            />
            <path
              d="M0,400 Q300,300 600,400 T1000,400"
              fill="none"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="2"
              className="animate-pulse"
              style={{ animationDelay: '1s' }}
            />
            <path
              d="M0,600 Q200,500 400,600 T1000,600"
              fill="none"
              stroke="rgba(255,255,255,0.25)"
              strokeWidth="2"
              className="animate-pulse"
              style={{ animationDelay: '2s' }}
            />
          </svg>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="space-y-8">
            {/* Logo */}
            <div className="flex items-center space-x-3 animate-fade-in">
              <img 
                src={logoRedMimbre} 
                alt="REDMIMBRE Logo" 
                className="w-20 h-20 lg:w-24 lg:h-24 object-contain filter drop-shadow-[0_10px_24px_rgba(0,0,0,0.35)]"
              />
            </div>

            {/* Welcome Message */}
            <div className="space-y-4 animate-slide-up">
              <h1 className="text-5xl font-bold leading-tight">
                {t.hello}
              </h1>
            </div>

            {/* Description
            <div className="max-w-md space-y-3 animate-fade-in-delay">
              <p className="text-lg text-blue-100 leading-relaxed">{t.brandDescription}</p>
            </div> */}
          </div>

          {/* Footer */}
          <div className="animate-fade-in">
            <p className="text-sm text-emerald-100">{t.footer}</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 bg-white flex flex-col">
        {/* Language Selector - Top Right */}
        {/* <div className="flex justify-end p-6 lg:p-8">
          <div className="flex items-center space-x-2 bg-gray-50 rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-100 transition-colors duration-200 cursor-pointer group">
            <Globe className="w-4 h-4 text-gray-500 group-hover:text-emerald-700 transition-colors" />
            <select
              value={language}
              onChange={(e) => onLanguageChange(e.target.value as "es" | "ca")}
              className="text-sm font-medium border-none bg-transparent focus:ring-0 focus:outline-none cursor-pointer text-gray-700 group-hover:text-emerald-700 transition-colors appearance-none pr-2"
            >
              <option value="es">Español</option>
              <option value="ca">Català</option>
            </select>
          </div>
        </div> */}

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-6 lg:px-16 py-8">
          <div className="w-full max-w-md space-y-8 animate-slide-up">
            {/* Header */}
            <div className="space-y-2">
              <h2 className="text-3xl font-bold text-gray-900">{t.welcomeBack}</h2>
              {!showForgotPassword && (
                <p className="text-sm text-gray-600">{t.createAccountPrompt}</p>
              )}
            </div>

            {/* Back Button for Forgot Password */}
            {showForgotPassword && (
              <button
                onClick={() => setShowForgotPassword(false)}
                className="flex items-center text-sm font-medium text-gray-600 hover:text-blue-600 transition-all duration-200 hover:translate-x-1 group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 transition-transform duration-200 group-hover:-translate-x-1" />
                {t.backToLogin}
              </button>
            )}

            {/* General Error Message */}
            {errors.general && (
              <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg animate-shake">
                <p className="text-sm font-medium text-red-700">{errors.general}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div className="space-y-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400 ml-1" />
                  </div>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 bg-transparent border-b-2 focus:outline-none transition-colors duration-200 ${
                      errors.email
                        ? "border-red-500 text-red-600"
                        : "border-gray-300 focus:border-blue-600 text-gray-900"
                    }`}
                    placeholder={t.enterEmail}
                    disabled={isLoading}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm font-medium text-red-600 animate-fade-in">{errors.email}</p>
                )}
              </div>

              {/* Password Field */}
              {!showForgotPassword && (
                <div className="space-y-2">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400 ml-1" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      className={`w-full pl-10 pr-12 py-3 bg-transparent border-b-2 focus:outline-none transition-colors duration-200 ${
                        errors.password
                          ? "border-red-500 text-red-600"
                          : "border-gray-300 focus:border-blue-600 text-gray-900"
                      }`}
                      placeholder={t.enterPassword}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title={showPassword ? t.hidePassword : t.showPassword}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-sm font-medium text-red-600 animate-fade-in">
                      {errors.password}
                    </p>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center px-6 py-4 bg-gray-900 text-white rounded-lg hover:bg-gray-800 focus:ring-4 focus:ring-gray-900/20 focus:outline-none disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 font-semibold text-base shadow-lg hover:shadow-xl transform hover:scale-[1.01] active:scale-[0.99] disabled:hover:scale-100"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                    <span>{showForgotPassword ? t.sending : t.loggingIn}</span>
                  </>
                ) : showForgotPassword ? (
                  t.sendResetLink
                ) : (
                  t.login
                )}
              </button>

              {/* Forgot Password Link */}
              {/* {!showForgotPassword && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors duration-200"
                  >
                    {t.forgotQuestion} <span className="text-blue-600 underline underline-offset-2">{t.clickHere}</span>
                  </button>
                </div>
              )} */}
            </form>
          </div>
        </div>

        {/* Entity Logos Section */}
        <div className="w-full px-4 lg:px-8 py-8 border-t border-gray-200">
          <div className="w-full max-w-4xl mx-auto space-y-6">
            {/* Logos Grid */}
            <div className="flex flex-wrap justify-center items-center gap-6">
              <img
                src={logoAmoverse}
                alt="Amoverse"
                className="h-16 w-24 object-contain opacity-80 hover:opacity-100 transition-opacity duration-200"
              />
              <img
                src={logoCentreSantJaume}
                alt="Centre Sant Jaume"
                className="h-16 w-24 object-contain opacity-80 hover:opacity-100 transition-opacity duration-200"
              />
              <img
                src={logoHogarSanJose}
                alt="Hogar San José"
                className="h-16 w-24 object-contain opacity-80 hover:opacity-100 transition-opacity duration-200"
              />
              <img
                src={logoNazaret}
                alt="Nazaret"
                className="h-16 w-24 object-contain opacity-80 hover:opacity-100 transition-opacity duration-200"
              />
              <img
                src={logoLaVinya}
                alt="La Vinya"
                className="h-16 w-24 object-contain opacity-80 hover:opacity-100 transition-opacity duration-200"
              />
              <img
                src={logoSalutAlta}
                alt="Salut Alta"
                className="h-16 w-24 object-contain opacity-80 hover:opacity-100 transition-opacity duration-200"
              />
              <img
                src={logoSectorSocial}
                alt="Sector Social"
                className="h-16 w-24 object-contain opacity-80 hover:opacity-100 transition-opacity duration-200"
              />
              <img
                src={logoPadreMontalvo}
                alt="Padre Montalvo"
                className="h-16 w-24 object-contain opacity-80 hover:opacity-100 transition-opacity duration-200"
              />
              {/* <img
                src={logoSectorSocial2}
                alt="Sector Social 2"
                className="h-12 object-contain opacity-80 hover:opacity-100 transition-opacity duration-200"
              /> */}
            </div>
            
            {/* Text */}
            <p className="text-center text-sm text-gray-600 font-medium">
              {t.accessibleApp}
            </p>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translateX(-5px);
          }
          20%, 40%, 60%, 80% {
            transform: translateX(5px);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-fade-in-delay {
          animation: fade-in 0.8s ease-out 0.2s both;
        }
        .animate-slide-up {
          animation: slide-up 0.8s ease-out;
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}
